import React, { useReducer, useState, Reducer, useCallback, useEffect, MouseEvent } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, compareAsc, format } from 'date-fns';
import styled, { css, createGlobalStyle } from 'styled-components';

import { MobileProps, SearchType } from '../../types';
import { SEARCH_TYPE_NAMES } from '../../constants';
import DurationTrip from './components/DurationTrip';
import Calendar from './components/Calendar';
import { convertDatesToStringWithEllipsis } from '../../utils';
import {
  Action,
  changeDates,
  changeShiftAction,
  setEndDateFocus,
  setStartDateFocus,
  ChangeShiftParams,
  DatePickerState,
  initialState,
  reducer,
  updateDatepickerState,
} from './state';
import { OFFSET_TIMEZONE_MSK, START_EVENING_HOUR } from './constants';

type DatePickerProps = {
  onChange?: (value: DatePickerState) => void;
  onClose: () => void;
  initialValue?: DatePickerState;
  open?: boolean;
  searchType: SearchType;
  className?: string;
  unavailableDates?: Date[];
  arrivalDatesDict?: Record<string, string[]>;
  isFlexDatesOff?: boolean;
  variant?: 'default' | 'hotel';
} & MobileProps;

export const DatePicker = ({
  onChange,
  isMobile,
  isWizard,
  initialValue,
  open,
  onClose,
  unavailableDates = [],
  arrivalDatesDict = {},
  searchType,
  className,
  isFlexDatesOff = false,
  variant = 'default',
}: DatePickerProps) => {
  const [state, dispatch] = useReducer<Reducer<DatePickerState, Action>>(
    reducer,
    initialValue || initialState
  );
  const { left, right, inputFocuses = { isStartDateFocus: false, isEndDateFocus: false } } = state;
  const { date: startDate, shift: startShift } = left;
  const { date: endDate, shift: endShift } = right;
  const { isStartDateFocus, isEndDateFocus } = inputFocuses;

  useEffect(() => {
    if (isWizard && !isStartDateFocus && !isEndDateFocus) {
      dispatch(setStartDateFocus());
    }
  }, [isWizard, state]);

  const openCalendar = () => {
    if (!isWizard) {
      setOpen(true);
    }
  };

  const handleClickStartInput = () => {
    dispatch(setStartDateFocus());
    openCalendar();
  };
  const handleClickEndInput = () => {
    dispatch(setEndDateFocus());
    openCalendar();
  };

  const [localOpen, setOpen] = useState(false);
  const isEvening = new Date().getUTCHours() + OFFSET_TIMEZONE_MSK >= START_EVENING_HOUR;
  const minDateTour = addDays(new Date(), isEvening ? 2 : 1);
  const minDate = searchType === SEARCH_TYPE_NAMES.PACKAGE ? minDateTour : new Date();

  const arrivalDates =
    (startDate &&
      arrivalDatesDict[format(startDate, 'yyyy-MM-dd')]?.map(
        (dateString) => new Date(dateString)
      )) ||
    [];
  const excludeShiftDates = startDate
    ? [...Array(startShift.right).keys()].map((num) => addDays(startDate, num + 1))
    : [];
  const excludeArrivalAndShiftDates = [...excludeShiftDates, ...arrivalDates];

  const excludeDates = isEndDateFocus ? excludeArrivalAndShiftDates : unavailableDates;

  useEffect(() => {
    setOpen(!!open);
    if (open) {
      dispatch(setStartDateFocus());
    }
  }, [open]);

  useEffect(() => {
    dispatch(updateDatepickerState(initialValue || initialState));
  }, [initialValue]);

  const closeCalendar = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    onClose();
  };

  const changeShift = useCallback(
    (shiftParams: ChangeShiftParams) => dispatch(changeShiftAction(shiftParams)),
    []
  );

  const onChangeCalendar = (date: any) => {
    const start = isStartDateFocus ? date : startDate;
    const end = isEndDateFocus ? date : endDate;
    const isReset = start && compareAsc(new Date(start), new Date(end)) > 0;

    const saveStart = isReset ? date : start;
    const saveEnd = isReset ? null : end;

    if (!isReset && isStartDateFocus) {
      dispatch(setEndDateFocus());
    }
    if (!isReset && isEndDateFocus) {
      dispatch(setStartDateFocus());
    }

    if (isReset) {
      dispatch(setEndDateFocus());
    }
    const abortCondition =
      format(new Date(saveStart), 'ddMMyy') === format(new Date(saveEnd), 'ddMMyy');

    if (abortCondition) return;

    if (saveStart !== startDate) {
      changeShift({ left: 0, right: 0 });
    }

    if (saveEnd !== endDate) {
      changeShift({ left: 0, right: 0, sideDate: 'right' });
    }

    dispatch(changeDates({ startDate: saveStart, endDate: saveEnd }));
  };

  const applyButtonHandler = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    closeCalendar(event);

    if (startDate && endDate && onChange) {
      onChange({ left, right, inputFocuses });
    }
  };

  return (
    <DatePickerContainer $isMobile={isMobile} $isWizard={isWizard} className={className}>
      {localOpen && isMobile && !isWizard ? <OverflowRestrictor /> : null}
      <DurationTrip
        leftValue={convertDatesToStringWithEllipsis({
          date: left.date,
          shift: left.shift,
        })}
        rightValue={convertDatesToStringWithEllipsis({
          date: right.date,
          shift: right.shift,
        })}
        openCalendar={openCalendar}
        isMobile={isMobile}
        isWizard={isWizard}
        searchType={searchType}
        isOpenCalendar={localOpen}
        onStartDateClick={handleClickStartInput}
        onEndDateClick={handleClickEndInput}
        isStartDateFocus={isStartDateFocus}
        isEndDateFocus={isEndDateFocus}
        variant={variant}
      />
      <Calendar
        open={localOpen}
        closeCalendar={applyButtonHandler}
        startDate={startDate}
        endDate={endDate}
        onChange={onChangeCalendar}
        startShift={startShift}
        endShift={endShift}
        changeShift={changeShift}
        excludeDates={excludeDates}
        applyButtonHandler={applyButtonHandler}
        minDate={minDate}
        isMobile={isMobile}
        isWizard={isWizard}
        isStartDateFocus={isStartDateFocus}
        isEndDateFocus={isEndDateFocus}
        isFlexDatesOff={isFlexDatesOff}
        isHotelOnly={searchType === SEARCH_TYPE_NAMES.HOTEL}
      />
    </DatePickerContainer>
  );
};

const DatePickerContainer = styled.div<{
  $isMobile?: boolean;
  $isWizard?: boolean;
}>`
  display: flex;
  flex-direction: column;
  order: 0;

  ${({ $isWizard }) =>
    !$isWizard &&
    `
    width: 100%;
    height: 100%;
  `}

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      overflow: hidden;
      order: 1;
      align-items: center;
    `}
`;

const OverflowRestrictor = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;
